import React from 'react';
import {
  Box,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Toolbar,
  Typography,
} from '@material-ui/core';
import SearchIcon from '@mui/icons-material/Search';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';

import { ChallengesQuery, ChallengesQueryVariables } from 'generated/graphql';
import { TableBox } from 'retired/TableBox';
import TablePaginationActions from 'retired/TablePaginationActions';
import { capitalizeFirstLetter } from 'utils/stringFormat';
import { getStatusChipProps } from 'components/Chip/chipHelpers';

import Filters, { FilterStatus } from './Filters';

interface ChallengeListProps {
  data?: ChallengesQuery;
  loading: boolean;
  error?: any;
  queryVariables: ChallengesQueryVariables;
  setQueryVariables: (variables: ChallengesQueryVariables) => void;
  searchValue: string;
  setSearchValue: (value: string) => void;
  selectedFilterStatus: FilterStatus;
  setSelectedFilterStatus: (status: FilterStatus) => void;
  onSortChange: (column: string) => void;
  currentSortColumn: string;
  currentSortOrder: 'asc' | 'desc';
}

export enum TableSortValues {
  title = 'title',
  status = 'status',
  startDate = 'startDate',
  endDate = 'endDate',
  createdAt = 'createdAt',
}

export const rowsPerPageOptions = [10, 25, 50];

const styles = {
  nameLink: {
    width: 200,
    display: 'block',
    lineHeight: '32px',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
  },
  tableHeader: {
    justifyContent: 'flex-start',
  },
  tableHeaderControls: {
    marginLeft: 'auto',
  },
  tableHeaderControl: {
    marginLeft: '16px',
  },
};

const ChallengeList: React.FC<ChallengeListProps> = ({
  data,
  loading,
  error,
  queryVariables,
  setQueryVariables,
  searchValue,
  setSearchValue,
  selectedFilterStatus,
  setSelectedFilterStatus,
  onSortChange,
  currentSortColumn,
  currentSortOrder,
}) => {
  const challenges = data?.challenges?.items || [];
  const challengesMetaData = data?.challenges?.metadata;

  const rowsPerPage = queryVariables?.take ?? rowsPerPageOptions[0];
  const page = queryVariables?.skip ? queryVariables.skip / rowsPerPage : 0;

  const handlePageChange = (_event: unknown, newPage: number): void => {
    if (!queryVariables) {
      return;
    }
    setQueryVariables({
      ...queryVariables,
      skip: newPage * queryVariables.take!,
    });
  };

  return (
    <>
      <Filters
        setQueryVariables={setQueryVariables}
        defaultQueryVariables={queryVariables}
        searchValue={searchValue}
        selectedFilterStatus={selectedFilterStatus}
        setSelectedFilterStatus={setSelectedFilterStatus}
      />
      <TableBox
        fixedBottom={
          <TablePagination
            rowsPerPageOptions={rowsPerPageOptions}
            colSpan={6}
            count={challengesMetaData?.totalItems || 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handlePageChange}
            onChangePage={handlePageChange}
            onRowsPerPageChange={(event): void => {
              if (!queryVariables) {
                return;
              }
              setQueryVariables({
                ...queryVariables,
                take: parseInt(event.target.value, 10),
                skip: 0,
              });
            }}
            ActionsComponent={TablePaginationActions as any}
          />
        }
      >
        <Toolbar style={styles.tableHeader}>
          <Typography
            variant="h6"
            style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            Challenges
          </Typography>
          <Box style={styles.tableHeaderControls}>
            <TextField
              type="search"
              placeholder="Search challenges..."
              style={{ width: '280px' }}
              variant="standard"
              InputProps={{
                startAdornment: <SearchIcon />,
              }}
              onChange={(e) => setSearchValue(e.target.value)}
              value={searchValue}
            />
          </Box>
        </Toolbar>
        <Table size="small">
          {!!challenges?.length && (
            <TableHead>
              <TableRow>
                <TableCell size="medium">
                  <TableSortLabel
                    direction={currentSortOrder}
                    active={currentSortColumn === TableSortValues.title}
                    onClick={(): void => {
                      onSortChange(TableSortValues.title);
                    }}
                  >
                    Title
                  </TableSortLabel>
                </TableCell>
                <TableCell size="small">
                  <TableSortLabel
                    direction={currentSortOrder}
                    active={currentSortColumn === TableSortValues.startDate}
                    onClick={(): void => {
                      onSortChange(TableSortValues.startDate);
                    }}
                  >
                    Start Date
                  </TableSortLabel>
                </TableCell>
                <TableCell size="small">
                  <TableSortLabel
                    direction={currentSortOrder}
                    active={currentSortColumn === TableSortValues.endDate}
                    onClick={(): void => {
                      onSortChange(TableSortValues.endDate);
                    }}
                  >
                    End Date
                  </TableSortLabel>
                </TableCell>
                <TableCell size="small">Employees Participating</TableCell>
                <TableCell size="small">Engaged Participants</TableCell>
                <TableCell size="small">
                  <TableSortLabel
                    direction={currentSortOrder}
                    active={currentSortColumn === TableSortValues.status}
                    onClick={(): void => {
                      onSortChange(TableSortValues.status);
                    }}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
                <TableCell size="small">Teams</TableCell>
              </TableRow>
            </TableHead>
          )}
          <TableBody>
            {loading ? (
              <LinearProgress />
            ) : error ? (
              <TableRow>
                <TableCell colSpan={7} style={{ textAlign: 'center' }}>
                  <Typography>There was an error loading challenges</Typography>
                </TableCell>
              </TableRow>
            ) : !challenges?.length ? (
              <TableRow>
                <TableCell colSpan={7} style={{ textAlign: 'center', paddingBottom: 20 }}>
                  <Typography>
                    {searchValue
                      ? `No challenges found matching "${searchValue}"`
                      : 'No challenges found with the current filters'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              challenges.map((challenge) => (
                <TableRow key={challenge.id}>
                  <TableCell>
                    <Link
                      to={`/challenges/${challenge.id}`}
                      className="hoverable"
                      style={styles.nameLink}
                    >
                      {challenge.title}
                    </Link>
                  </TableCell>

                  <TableCell>
                    {challenge.startDate
                      ? format(new Date(challenge.startDate), 'MMM dd, yyyy')
                      : '-'}
                  </TableCell>
                  <TableCell>
                    {challenge.endDate ? format(new Date(challenge.endDate), 'MMM dd, yyyy') : '-'}
                  </TableCell>
                  <TableCell>{challenge.generalStats?.totalParticipants || 0}</TableCell>
                  <TableCell>{challenge.generalStats?.totalUsersWithActionsCompleted || 0}</TableCell>
                  <TableCell>
                    <Chip
                      label={capitalizeFirstLetter(challenge.status || '')}
                      size="small"
                      color="default"
                      {...getStatusChipProps(challenge.status)}
                    />
                  </TableCell>
                  <TableCell>
                    {challenge.generalStats?.totalTeams
                      ? `${challenge.generalStats.totalTeams} teams`
                      : 'No teams'}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableBox>
    </>
  );
};

export default ChallengeList;
